import { useId, useState } from 'react';
import type { GarmentCategory, GarmentViewKind } from '@studio/shared';

/**
 * The photograph to take: one plain white t-shirt, photographed the way each
 * view asks for — on a hanger from the front, from the back, from the side,
 * a close-up of a detail, the care label. Shown where a photograph is wanted
 * and not there yet, so "what should I upload?" is answered by a picture
 * rather than a sentence. The same shirt in every view, so the five read as
 * one piece seen five ways. Trousers are shown with a pair of jeans, and
 * shoes with a pair of white trainers.
 *
 * If a photograph cannot be loaded, the same shirt drawn stands in for it.
 */
export function GarmentExample({
  view,
  category,
  className,
}: {
  view: GarmentViewKind;
  category?: GarmentCategory;
  className?: string;
}) {
  const src = `/garment-examples/${exampleFile(view, category)}.jpg`;
  // Keyed by the photograph, so a piece that changes kind gets another try.
  return <ExamplePhoto key={src} src={src} view={view} className={className} />;
}

/**
 * Which photograph shows a view. Trousers and shoes have their own front,
 * back and side — a pair of jeans on a hanger, a pair of white trainers —
 * because a t-shirt says nothing about how to hang a waistband or which end
 * of a shoe is its back. A close-up and a care label read the same on any
 * piece, so those stay the t-shirt's.
 */
const OWN_VIEWS: Partial<Record<GarmentCategory, GarmentViewKind[]>> = {
  bottom: ['front', 'back', 'side'],
  shoes: ['front', 'back', 'side'],
};

function exampleFile(view: GarmentViewKind, category?: GarmentCategory): string {
  return category && OWN_VIEWS[category]?.includes(view) ? `${category}-${view}` : view;
}

function ExamplePhoto({ src, view, className }: { src: string; view: GarmentViewKind; className?: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <GarmentDrawing view={view} className={className} />;
  return (
    <img
      className={`gex gex--photo ${className ?? ''}`.trim()}
      src={src}
      alt=""
      aria-hidden="true"
      decoding="async"
      draggable={false}
      onError={() => setFailed(true)}
    />
  );
}

/**
 * The same white t-shirt, drawn: it weighs nothing and stays sharp at any
 * size; its colours are the photograph's, not the theme's, in either room.
 */
function GarmentDrawing({ view, className }: { view: GarmentViewKind; className?: string }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg
      className={`gex ${className ?? ''}`.trim()}
      viewBox="0 0 120 150"
      role="img"
      aria-hidden="true"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id={`${id}-sweep`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#d6d6da" />
          <stop offset="1" stopColor="#f2f2f4" />
        </linearGradient>
        <linearGradient id={`${id}-cloth`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="1" stopColor="#ececee" />
        </linearGradient>
        <pattern id={`${id}-weave`} width="4" height="4" patternUnits="userSpaceOnUse">
          <path d="M0 2h4M2 0v4" stroke="#e2e2e5" strokeWidth="0.6" />
        </pattern>
      </defs>
      <rect width="120" height="150" fill={`url(#${id}-sweep)`} />
      {view === 'detail' ? (
        <Detail id={id} />
      ) : view === 'label' ? (
        <Label id={id} />
      ) : view === 'side' ? (
        <Side id={id} />
      ) : (
        <Tee id={id} back={view === 'back'} />
      )}
    </svg>
  );
}

const STROKE = '#c4c4c8';

/** Front or back: the same shirt, the neckline cut deep in front and shallow behind. */
function Tee({ id, back }: { id: string; back: boolean }) {
  return (
    <g>
      <ellipse cx="60" cy="128" rx="30" ry="3.5" fill="#000" opacity="0.06" />
      <path
        d={`M50 36 L36 40 L20 57 L28 67 L36 60 L35 124 L85 124 L84 60 L92 67 L100 57 L84 40 L70 36 Q60 ${back ? 40 : 47} 50 36 Z`}
        fill={`url(#${id}-cloth)`}
        stroke={STROKE}
        strokeWidth="1.1"
        strokeLinejoin="round"
      />
      {/* The collar rib, and the soft folds a hung shirt falls into. */}
      <path
        d={back ? 'M51 37.5 Q60 41.5 69 37.5' : 'M51.5 38 Q60 48.5 68.5 38'}
        fill="none"
        stroke={STROKE}
        strokeWidth="0.9"
      />
      <path d="M45 70 Q47 95 44 118" fill="none" stroke="#e3e3e6" strokeWidth="1.2" />
      <path d="M74 72 Q72 96 76 118" fill="none" stroke="#e3e3e6" strokeWidth="1.2" />
      <path d="M28 67 L36 60" fill="none" stroke={STROKE} strokeWidth="0.8" />
      <path d="M92 67 L84 60" fill="none" stroke={STROKE} strokeWidth="0.8" />
    </g>
  );
}

/** The side: the body seen edge-on, a sleeve standing off it, the side seam down the middle. */
function Side({ id }: { id: string }) {
  return (
    <g>
      <ellipse cx="60" cy="128" rx="20" ry="3" fill="#000" opacity="0.06" />
      <path
        d="M50 38 Q60 34 70 38 L73 124 L47 124 Z"
        fill={`url(#${id}-cloth)`}
        stroke={STROKE}
        strokeWidth="1.1"
        strokeLinejoin="round"
      />
      <path
        d="M64 42 L82 62 L75 69 L63 56 Z"
        fill={`url(#${id}-cloth)`}
        stroke={STROKE}
        strokeWidth="1.1"
        strokeLinejoin="round"
      />
      <path d="M60 37 L60 124" fill="none" stroke={STROKE} strokeWidth="0.8" strokeDasharray="2 1.6" />
    </g>
  );
}

/** A close-up: the neckline's rib and its double row of stitches, the knit showing. */
function Detail({ id }: { id: string }) {
  return (
    <g>
      <rect x="0" y="0" width="120" height="150" fill={`url(#${id}-cloth)`} />
      <rect x="0" y="0" width="120" height="150" fill={`url(#${id}-weave)`} />
      <path
        d="M-10 70 Q60 128 130 70 L130 92 Q60 150 -10 92 Z"
        fill="#f7f7f8"
        stroke={STROKE}
        strokeWidth="1"
      />
      <path d="M-10 77 Q60 135 130 77" fill="none" stroke="#9b9ba1" strokeWidth="0.9" strokeDasharray="3 2" />
      <path d="M-10 85 Q60 143 130 85" fill="none" stroke="#9b9ba1" strokeWidth="0.9" strokeDasharray="3 2" />
      <circle cx="60" cy="60" r="34" fill="none" stroke="#fff" strokeWidth="2" opacity="0.9" />
      <circle cx="60" cy="60" r="34" fill="none" stroke={STROKE} strokeWidth="0.8" strokeDasharray="1 3" />
    </g>
  );
}

/** The care label, sewn into the back of the neck. */
function Label({ id }: { id: string }) {
  return (
    <g>
      <path
        d="M-5 40 Q60 78 125 40 L125 150 L-5 150 Z"
        fill={`url(#${id}-cloth)`}
        stroke={STROKE}
        strokeWidth="1"
      />
      <path d="M-5 46 Q60 84 125 46" fill="none" stroke={STROKE} strokeWidth="0.8" />
      <rect x="44" y="62" width="32" height="44" rx="2" fill="#ffffff" stroke={STROKE} strokeWidth="1" />
      <rect
        x="47"
        y="65"
        width="26"
        height="38"
        rx="1"
        fill="none"
        stroke="#d3d3d7"
        strokeWidth="0.6"
        strokeDasharray="1.5 1.2"
      />
      <rect x="51" y="71" width="18" height="2.4" rx="1" fill="#9b9ba1" />
      <rect x="51" y="77" width="14" height="1.8" rx="0.9" fill="#c4c4c8" />
      <rect x="51" y="82" width="16" height="1.8" rx="0.9" fill="#c4c4c8" />
      <circle cx="54" cy="91" r="2.2" fill="none" stroke="#9b9ba1" strokeWidth="0.7" />
      <path d="M59 89 l4 4 m0 -4 l-4 4" stroke="#9b9ba1" strokeWidth="0.7" />
      <rect x="65" y="89" width="4.4" height="4.4" fill="none" stroke="#9b9ba1" strokeWidth="0.7" />
    </g>
  );
}
